import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { ExternalLink } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import SubpageHero from "@/components/landing/SubpageHero";
import FinalCTA from "@/components/landing/FinalCTA";

const BOOKS_IMAGE =
  "https://customer-assets.emergentagent.com/job_7454cbe9-4bab-46e2-a1d4-503c4cbbe3d0/artifacts/e0oxle5x_20240510RockSocialCoverPhotosTractionx1.jpg";

const FOUNDATIONAL = [
  {
    titleEn: "Traction: Get a Grip on Your Business",
    titleEs: "Tracción — Controla tu negocio",
    authors: "Gino Wickman",
    year: "2007 / 2012",
    desc: "El libro fundacional de EOS. Presenta el sistema completo con sus herramientas prácticas para que dueños y equipos de liderazgo recuperen el control, ganen foco y disfruten más del negocio. Cubre los Seis Componentes Clave: Visión, Personas, Datos, Asuntos, Procesos y Tracción. La base imprescindible para cualquier líder que quiera implementar EOS en Chile o Latinoamérica.",
  },
  {
    titleEn: "Get a Grip: An Entrepreneurial Fable",
    titleEs: "Agárrate — Una fábula empresarial",
    authors: "Gino Wickman & Mike Paton",
    year: "2012",
    desc: "La versión narrativa de Traction, contada como una fábula empresarial con personajes y situaciones realistas. Muestra cómo se implementa EOS paso a paso en una empresa ficticia, facilitando visualizar y traducir las herramientas a la propia realidad. Ideal para quienes prefieren aprender a través de historias en lugar de manuales técnicos.",
  },
  {
    titleEn: "Rocket Fuel",
    titleEs: "Combustible de Cohete — El dúo Visionario + Integrador",
    authors: "Gino Wickman & Mark C. Winters",
    year: "2015",
    desc: "Explica los dos roles clave en la cima de toda empresa exitosa: el Visionario (generador de ideas y dirección) y el Integrador (ejecutor que mantiene cohesionado al equipo). Detalla cómo una relación efectiva entre ambos impulsa a cualquier organización a su siguiente nivel. Lectura obligada para fundadores y sus segundos al mando.",
  },
  {
    titleEn: "How to Be a Great Boss",
    titleEs: "Cómo ser un gran jefe",
    authors: "Gino Wickman & Renée Boer",
    year: "2016",
    desc: "Aborda la desconexión laboral desde la perspectiva del liderazgo: la diferencia entre un equipo indiferente y uno comprometido suele estar en tener un gran jefe. Ofrece un enfoque práctico para líderes en cualquier nivel sobre cómo gestionar, dirigir y obtener lo mejor de sus colaboradores, todo desde la filosofía EOS.",
  },
  {
    titleEn: "What the Heck is EOS?",
    titleEs: "¿Qué diablos es EOS?",
    authors: "Gino Wickman & Tom Bouwer",
    year: "2017",
    desc: "Guía sencilla diseñada para que todos los colaboradores —no solo el equipo de liderazgo— entiendan cómo funciona EOS y cuál es su rol dentro de la metodología. Usa lenguaje directo, sin tecnicismos. Fundamental para alinear a toda la organización cuando una empresa ya trabaja con EOS.",
  },
  {
    titleEn: "The EOS Life",
    titleEs: "La vida EOS",
    authors: "Gino Wickman",
    year: "2021",
    desc: "Sale del enfoque puramente empresarial para centrarse en el estilo de vida ideal: hacer lo que amas, con personas que amas, generando un gran impacto, con la compensación adecuada y tiempo para tus otras pasiones. Una guía personal para diseñar y vivir una vida 100% personalizada y alcanzable. Incluye Journal + Planner complementario.",
  },
];

const MASTERY = [
  {
    titleEn: "Process!",
    titleEs: "Procesos — Libera el potencial de tu empresa",
    authors: "Mike Paton & Lisa González",
    year: "2022",
    badge: "01",
    desc: "Primer libro de la EOS Mastery Series. Profundiza en el Componente de Procesos — uno de los más malentendidos. Muestra cómo dominar los procesos libera capacidad para pensar en grande, innovar y crecer, en lugar de seguir apagando incendios y reinventando rutinas cada vez.",
  },
  {
    titleEn: "People: Dare to Build an Intentional Culture",
    titleEs: "Personas — Construye una cultura intencional",
    authors: "Mark O'Donnell, Kelly Knight & CJ DuBe'",
    year: "2024",
    badge: "02",
    desc: "Segundo libro de la EOS Mastery Series. Profundiza en el Componente de Personas y enseña cómo construir una cultura intencional, no por accidente. Presenta el enfoque centrado en las personas de EOS para alinear colaboradores con la visión y construir un equipo cohesionado y auto-sostenible.",
  },
  {
    titleEn: "Data: Harness Your Numbers to Go from Uncertain to Unstoppable",
    titleEs: "Datos — Lidera con la verdad de los números",
    authors: "Mark O'Donnell, Angela Kalemis & Mark Stanley",
    year: "2025",
    badge: "03",
    desc: "Tercer libro de la EOS Mastery Series. Enseña a construir un Scorecard que realmente funcione, identificar las métricas que impulsan el negocio e implementar mediciones para cada miembro del equipo. El mensaje central: deja de liderar con suposiciones y empieza a liderar con datos.",
  },
  {
    titleEn: "Issues: Remove Friction, Fast Track Your Growth, and Ignite Your Greatness",
    titleEs: "Asuntos — Resuelve de raíz, no los síntomas",
    authors: "Mark O'Donnell, Sue Hawkes & Jill Young",
    year: "2026",
    badge: "04",
    desc: "Cuarto libro de la EOS Mastery Series. Enfocado en el Componente de Asuntos. Aborda los equipos atrapados en reuniones que rehúsan los mismos problemas sin resolverlos y enseña a llegar a la raíz real. Cómo construir una cultura de alta confianza que transforme reuniones improductivas en sesiones de 90 minutos con resultados.",
  },
];

function BookCard({ book, delay = 0 }) {
  return (
    <article
      data-testid={`book-card-${book.titleEn.substring(0, 10).replace(/\s+/g, "-").toLowerCase()}`}
      className="reveal relative rounded-2xl border border-slate-200 bg-white p-6 lg:p-7 hover:border-[#F26522]/40 hover:shadow-xl transition-all duration-300 overflow-hidden"
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-[#F26522] to-[#F26522]/40" />
      <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#F26522] font-body">
        {book.titleEn}
      </p>
      <h3 className="mt-2 font-display font-extrabold text-xl text-navy-800 leading-snug">
        {book.titleEs}
      </h3>
      <p className="mt-1.5 text-sm text-slate-400 font-medium">
        {book.authors} · {book.year}
      </p>
      <p className="mt-3 text-slate-600 leading-relaxed text-sm">{book.desc}</p>
    </article>
  );
}

function MasteryCard({ book, delay = 0 }) {
  return (
    <article
      data-testid={`mastery-card-${book.badge}`}
      className="reveal relative rounded-2xl border border-[#F26522]/15 bg-[#FEF3EB] p-6 lg:p-7 hover:border-[#F26522]/40 hover:shadow-lg transition-all duration-300 overflow-hidden"
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#F26522]">
          Mastery Series · {book.badge} / 04
        </span>
        <span className="text-xs font-medium text-slate-400">{book.year}</span>
      </div>
      <p className="mt-3 text-xs font-bold tracking-[0.14em] uppercase text-slate-400">
        {book.titleEn}
      </p>
      <h3 className="mt-1.5 font-display font-extrabold text-xl text-navy-800 leading-snug">
        {book.titleEs}
      </h3>
      <p className="mt-1 text-sm text-slate-500 font-medium">{book.authors}</p>
      <p className="mt-3 text-navy-800/70 leading-relaxed text-sm">{book.desc}</p>
    </article>
  );
}

export default function LibrosEOS() {
  useReveal();

  useEffect(() => {
    setPageSEO({
      title: "La Biblioteca EOS | Libros de Gino Wickman | Traction | Carlos Garcín Chile",
      description: "Conoce la Biblioteca EOS completa: Traction, Rocket Fuel, Get a Grip y la EOS Mastery Series de Gino Wickman, los libros clave de la metodología.",
      path: "/libros-eos",
      keywords: "libros EOS, Traction Gino Wickman, Rocket Fuel, EOS Mastery Series, biblioteca EOS",
    });
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <main>
        <SubpageHero
          eyebrow="La Biblioteca EOS — Traction Library"
          title={
            <>
              Los libros que construyeron{" "}
              <span className="text-[#F26522]">el movimiento EOS</span>
            </>
          }
          subtitle="Gino Wickman y otros autores de EOS han desarrollado una biblioteca completa que cubre cada componente de la metodología. Desde el libro fundacional Traction hasta la EOS Mastery Series, aquí está todo lo que necesitas para entender y profundizar en EOS."
          withCta
        />

        {/* Imagen de la biblioteca */}
        <section className="bg-white pt-14 sm:pt-16 pb-0">
          <div className="container-eos">
            <div className="reveal rounded-2xl overflow-hidden shadow-xl shadow-slate-200/60 border border-slate-100">
              <img
                src={BOOKS_IMAGE}
                alt="Biblioteca EOS — Traction Library de Gino Wickman: todos los libros de la metodología EOS"
                className="w-full object-cover max-h-[420px] object-top"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* Libros fundacionales */}
        <section data-testid="libros-fundacionales" className="section-padding bg-white">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">Traction Library — Libros fundacionales</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 leading-tight">
                Los libros que definen{" "}
                <span className="text-[#F26522]">la metodología EOS</span>
              </h2>
              <p className="mt-5 text-lg text-slate-600 max-w-2xl">
                Escritos por Gino Wickman y sus colaboradores, estos libros son la fuente original de EOS — los conceptos de Visión, Tracción y liderazgo que transforman empresas en Chile y el mundo.
              </p>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
              {FOUNDATIONAL.map((book, i) => (
                <BookCard key={book.titleEn} book={book} delay={i * 70} />
              ))}
            </div>

            {/* Lead magnet: descarga gratis Capítulo 1 de Tracción */}
            <div className="mt-8 reveal rounded-2xl border border-[#F26522]/20 bg-gradient-to-r from-[#FEF3EB] to-white p-7 lg:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#F26522]">Antes de comprarlo</p>
                <p className="mt-2 font-display font-extrabold text-xl text-navy-800 leading-tight">
                  Lee gratis el Capítulo 1 de Tracción
                </p>
                <p className="mt-1.5 text-slate-600 text-sm max-w-md">
                  Recíbelo en tu correo al instante, sin costo.
                </p>
              </div>
              <a
                href="/libro-traccion-gino-wickman"
                data-testid="libros-eos-lead-magnet-link"
                className="btn-primary shrink-0"
              >
                Descargar Capítulo 1 gratis
              </a>
            </div>
          </div>
        </section>

        {/* EOS Mastery Series */}
        <section data-testid="mastery-series" className="section-padding bg-slate-50/60">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">EOS Mastery Series — Profundidad por componente</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 leading-tight">
                Un libro dedicado a cada{" "}
                <span className="text-[#F26522]">componente del modelo</span>
              </h2>
              <p className="mt-5 text-lg text-slate-600 max-w-2xl">
                La EOS Mastery Series profundiza en cada uno de los Seis Componentes Clave. Perfecta para equipos que ya implementan EOS y quieren llevar un área específica al siguiente nivel.
              </p>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
              {MASTERY.map((book, i) => (
                <MasteryCard key={book.titleEn} book={book} delay={i * 80} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA comprar libros */}
        <section data-testid="libros-buy-cta" className="py-16 bg-white">
          <div className="container-eos">
            <div className="reveal rounded-2xl border border-[#F26522]/20 bg-[#FEF3EB] p-8 lg:p-10 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <p className="font-display font-extrabold text-2xl text-navy-800 leading-tight">
                  ¿Quieres comprar los libros de la Biblioteca EOS?
                </p>
                <p className="mt-2 text-slate-600">
                  Puedes adquirirlos directamente en el sitio web oficial de EOS Worldwide.
                </p>
              </div>
              <a
                href="https://www.eosworldwide.com/eos-books"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="eos-books-external-link"
                className="btn-primary shrink-0 flex items-center gap-2"
              >
                Ver en EOS Worldwide
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        <FinalCTA />
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
    </>
  );
}
